import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    CircularProgress,
    Container,
    Divider,
    Grid,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import SelectField from '../../../components/ui/SelectField';
import TextareaField from '../../../components/ui/TextareaField';
import DateRangeField from '../../../components/ui/DateRangeField';
import { getAvailableBookingTimes } from '../../../utils/dateTimeUtils';
import { sortByLocaleName } from '../../../utils';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { stationDateTime } from '../../../utils/stationTime';
import { useLocationTimeZone } from '../../../hooks/useLocationTimeZone';
import { useGetLocationListQuery } from '../../../api/locations/locationsApiSlice';
import { useTranslation } from 'react-i18next';
import SnackbarComponent from '../../common/SnackbarComponent';
import { CreateRoomRentalBookingPayload } from '../../../types/bookingCreation';
import { useNavigate } from 'react-router';
import { paths } from '../../../routes';
import FilteredSelect from '../../common/FilteredSelect';
import CustomButton from '../../../components/CustomButton';
import { useCreateRoomRentalBooking } from '../../../flows/createRoomRentalBookingFlow';
import { useGetAssetsAvailabilityDataQuery } from '../../../api/booking/bookingApiSlice';
import { AssetAvailability } from '../../../api/types';
import { blockerUseCaseOf } from './blockerUseCase';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { useDispatch } from 'react-redux';
import {
    clearBookingCreationStates,
    clearCheckOutDate,
    setCheckInDate,
    setCheckOutDate,
    setCity,
    setComment,
    setEndTime,
    setStartTime,
} from '../../../store/slices/bookingCreationSlice';
import { useFetchBooking } from '../../../flows/fetchBookingsFlow';
import PageHeader from '../../../components/PageHeader';
import PageHeadline from '../../../components/PageHeadline';
import { isRoomFamily, spaceOf } from '../../../utils/useCases';

dayjs.extend(isSameOrBefore);

// A picked day (and, for a venue, hour) on the station's clock (ADR-0019) —
// what the blocker stores and what the overlap check compares. An apartment
// blocker is station midnight, not the browser's — on the clock of the
// blocked asset's location (#3219).
const blockerDateTime = (date: Dayjs, time: string | null | undefined, timeZone: string): Dayjs =>
    stationDateTime(date, time || '00:00', timeZone);

const BLOCKING_REASONS = [
    'furniture_fixtures',
    'bathroom_sanitary',
    'kitchen_appliances',
    'hvac',
    'electrical_systems',
    'plumbing_issues',
    'internet_connectivity',
    'cleaning_housekeeping',
    'pest_control',
    'safety_security',
    'exterior_outdoor',
    'noise_neighbor',
    'problem_with_app',
    'other_miscellaneous',
];

const CreateBlockerPage = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [cities, setCities] = useState<string[]>([]);
    const [loadingCities, setLoadingCities] = useState(true);
    const [loadingCreate, setLoadingCreate] = useState(false);
    const [reason, setReason] = useState('');
    const [selectedAsset, setSelectedAsset] = useState<AssetAvailability | null>(null);
    const timeZone = useLocationTimeZone(selectedAsset?.locationId);
    const [snackbarData, setSnackbarData] = useState({
        open: false,
        message: '',
        severity: 'info' as 'success' | 'info' | 'warning' | 'error',
    });

    const config = useSelector(
        (state: RootState) => state.authentication.config,
    );
    const selectedTenant = useSelector(
        (state: RootState) => state.authentication.tenant,
    );
    const useCases = config?.services;

    const createRoomRentalBooking = useCreateRoomRentalBooking();
    const fetchBooking = useFetchBooking(useCases ?? ['']);

    const city = useSelector(
        (state: RootState) => state.bookingCreation.orderData.city,
    );
    const startDate = useSelector(
        (state: RootState) => state.bookingCreation.orderData.checkInDate,
    );
    const endDate = useSelector(
        (state: RootState) => state.bookingCreation.orderData.checkOutDate,
    );
    const startTime = useSelector(
        (state: RootState) => state.bookingCreation.orderData.startTime,
    );
    const endTime = useSelector(
        (state: RootState) => state.bookingCreation.orderData.endTime,
    );
    const comment = useSelector(
        (state: RootState) => state.bookingCreation.orderData.comment,
    );

    const { data: apartmentLocations } = useGetLocationListQuery('apartment');
    const { data: venueLocations } = useGetLocationListQuery('venue');
    // A sports hall is blocked like a venue (closures, maintenance).
    const { data: hallLocations } = useGetLocationListQuery('hall');

    // Full, date-independent asset roster so the object can be picked before the
    // date. The statistics endpoint returns every asset regardless of bookings;
    // we only use it for the object list, not for availability filtering.
    const rosterYear = dayjs().format('YYYY');
    const rosterMonth = dayjs().format('MM');
    const {
        data: assetsData = [],
        isLoading: loadingAssets,
        isError: assetsFailed,
        refetch: refetchAssets,
    } = useGetAssetsAvailabilityDataQuery({
        data: { year: rosterYear, month: rosterMonth },
    });


    useEffect(() => {
        dispatch(clearBookingCreationStates());
    }, []);

    // Tenant change requires manual refetch (tenant is in auth headers, not query args).
    useEffect(() => {
        if (selectedTenant) {
            refetchAssets();
        }
    }, [selectedTenant]);

    useEffect(() => {
        const allLocations = [
            ...(apartmentLocations || []),
            ...(venueLocations || []),
            ...(hallLocations || []),
        ];
        if (allLocations.length > 0) {
            setCities(
                Array.from(
                    new Set(allLocations.map((loc) => loc.address.city)),
                ),
            );
            setLoadingCities(false);
        }
    }, [apartmentLocations, venueLocations, hallLocations]);

    useEffect(() => {
        if (!loadingCities && cities?.length === 1 && !city) {
            dispatch(setCity({ city: cities[0] }));
        }
    }, [cities, city, loadingCities, dispatch]);

    const cityByLocationId = useMemo(() => {
        const map: Record<string, string> = {};
        [...(apartmentLocations || []), ...(venueLocations || []), ...(hallLocations || [])].forEach((loc) => {
            map[loc.id] = loc.address.city;
        });
        return map;
    }, [apartmentLocations, venueLocations, hallLocations]);

    const objectsInCity = useMemo(() => {
        if (!city) return [];
        return assetsData.filter(
            (asset) =>
                asset.useCases?.some((uc) => isRoomFamily(uc)) &&
                cityByLocationId[asset.locationId] === city,
        );
    }, [assetsData, cityByLocationId, city]);

    const objectNames = useMemo(
        () => sortByLocaleName(objectsInCity.map((asset) => asset.assetName), (name) => name),
        [objectsInCity],
    );

    // Apartments are booked daily, venues (event rooms) hourly — mirror the
    // regular booking flow and offer time selection only for rooms booked by
    // the hour.
    const useCaseForAsset = blockerUseCaseOf(selectedAsset);
    const isVenue = spaceOf(useCaseForAsset)?.timeUnit === 'hour';

    const clearTimes = () => {
        dispatch(setStartTime({ startTime: '' }));
        dispatch(setEndTime({ endTime: '' }));
    };

    const handleCityChange = (selectedCity: string) => {
        dispatch(setCity({ city: selectedCity }));
        setSelectedAsset(null);
        clearTimes();
    };

    const handleObjectChange = (assetName: string) => {
        const asset = objectsInCity.find((a) => a.assetName === assetName);
        setSelectedAsset(asset ?? null);
        clearTimes();
    };

    const handleDateChange = (start: Dayjs | null, end: Dayjs | null) => {
        if (start) {
            dispatch(setCheckInDate({ checkInDate: start.startOf('day') }));
            dispatch(clearCheckOutDate());
        }
        if (end) {
            dispatch(setCheckOutDate({ checkOutDate: end }));
        }
    };

    const handleStartTimeChange = (value: string) => {
        dispatch(setStartTime({ startTime: value }));
    };

    const handleEndTimeChange = (value: string) => {
        dispatch(setEndTime({ endTime: value }));
    };

    const generateAvailableDates = (minDate?: Dayjs | null) => {
        const start = minDate || dayjs().startOf('day');
        const end = dayjs().add(18, 'month').endOf('month').startOf('day');
        const dates: string[] = [];
        for (let date = start; date.isBefore(end); date = date.add(1, 'day')) {
            dates.push(date.format('YYYY-MM-DD'));
        }
        return dates;
    };

    const generateAvailableCheckOutDates = (checkInDate: Dayjs | null) => {
        if (!checkInDate) return generateAvailableDates(dayjs());
        // Venues can be blocked within a single day (hourly); apartments need
        // at least one night, so their checkout starts the next day.
        const start = isVenue ? checkInDate : checkInDate.add(1, 'day');
        const end = dayjs().add(18, 'month').endOf('month').startOf('day');
        const dates: string[] = [];
        for (let date = start; date.isBefore(end); date = date.add(1, 'day')) {
            dates.push(date.format('YYYY-MM-DD'));
        }
        return dates;
    };

    const availableCheckInDates = generateAvailableDates(dayjs());
    const availableCheckOutDates = generateAvailableCheckOutDates(startDate);

    let availableStartTimes: string[] = [];
    let availableEndTimes: string[] = [];
    if (isVenue && startDate && endDate) {
        const times = getAvailableBookingTimes(startTime, startDate, endDate, timeZone);
        availableStartTimes = times.availableStartTimes;
        availableEndTimes = times.availableEndTimes;
    }

    const isFormValid =
        !!city &&
        !!selectedAsset &&
        !!startDate &&
        !!endDate &&
        !!reason &&
        (!isVenue || (!!startTime && !!endTime));

    const nightCount = startDate && endDate ? dayjs(endDate).diff(dayjs(startDate), 'day') : 0;

    const handleCreateBlocker = async () => {
        if (!isFormValid) return;

        setLoadingCreate(true);

        // For venues, fold the chosen hour into the check-in/out timestamps and
        // pass the times through; apartments stay day-level.
        const payload: CreateRoomRentalBookingPayload = {
            checkIn: blockerDateTime(startDate!, isVenue ? startTime : null, timeZone),
            checkOut: blockerDateTime(endDate!, isVenue ? endTime : null, timeZone),
            ...(isVenue ? { startTime, endTime } : {}),
            city: city ?? '',
            numberOfGuests: 1,
            apartmentId: selectedAsset?.assetId ?? '',
            locationId: selectedAsset?.locationId ?? '',
            additionalServices: [],
            customerDetails: {
                firstName: 'Blocker',
                lastName: '',
                email: 'blocker@kolula.com',
                phone: '+0000000000',
                address: '-',
                country: '-',
                city: '-',
                zipCode: '-',
                company: '-',
            },
            someoneElseDetails: {},
            useCase: useCaseForAsset,
            discount: 0,
            comment: comment ?? '',
            isFakeForBlocking: true,
            reasonForBlocking: reason,
        };

        const { success, reason: refusal } = await createRoomRentalBooking(payload, 'save_only');

        if (success) {
            setSnackbarData({
                open: true,
                message: t('create_blocker.response.success'),
                severity: 'success',
            });
            setTimeout(() => {
                fetchBooking();
                navigate(paths.rentalBooking);
            }, 1500);
            dispatch(clearBookingCreationStates());
        } else {
            setSnackbarData({
                open: true,
                message: t(refusal === 'slot_taken' ? 'create_blocker.response.slot_taken' : 'create_blocker.response.failure'),
                severity: 'error',
            });
        }
        setLoadingCreate(false);
    };

    const handleCloseSnackbar = () => {
        setSnackbarData((prev) => ({ ...prev, open: false }));
    };

    const styles = {
        paper: {
            padding: '24px',
        },
    };

    const renderSummaryValue = (value: string | null) =>
        value ? (
            <Typography variant={'body1'} fontWeight={500}>
                {value}
            </Typography>
        ) : (
            <Typography
                variant={'body1'}
                sx={{ fontStyle: 'italic', color: 'text.disabled' }}
            >
                {t('common.not_applicable')}
            </Typography>
        );

    return (
        <Container maxWidth={false}>
            <Box sx={{ py: 3 }}>
                <PageHeader>
                    <PageHeadline>{t('create_blocker.title')}</PageHeadline>
                </PageHeader>
                <Typography variant={'body2'} color={'text.secondary'} sx={{ mt: 0.5, mb: 3 }}>
                    {t('create_blocker.description')}
                </Typography>

                <Grid container spacing={3}>
                    {/* Main Form */}
                    <Grid item xs={12} lg={8}>
                        <Paper sx={styles.paper}>
                            <Stack spacing={3}>
                                {/* Step 1: Location */}
                                <Box>
                                    <Typography variant={'subtitle1'} fontWeight={600} sx={{ mb: 2 }}>
                                        {t('create_blocker.select_location')}
                                    </Typography>
                                    {loadingCities ? (
                                        <CircularProgress size={24} />
                                    ) : (
                                        <FilteredSelect
                                            options={cities}
                                            selectedOption={city ?? ''}
                                            handleOptionChange={handleCityChange}
                                            label={t('create_blocker.select_location') + ' *'}
                                        />
                                    )}
                                </Box>

                                <Divider />

                                {/* Step 2: Object selection */}
                                <Box>
                                    <Typography variant={'subtitle1'} fontWeight={600} sx={{ mb: 2 }}>
                                        {t('create_blocker.select_apartment')}
                                    </Typography>
                                    {loadingAssets ? (
                                        <CircularProgress size={24} />
                                    ) : (
                                        <FilteredSelect
                                            options={objectNames}
                                            selectedOption={selectedAsset?.assetName ?? ''}
                                            handleOptionChange={handleObjectChange}
                                            label={t('create_blocker.select_apartment') + ' *'}
                                        />
                                    )}
                                    {/* A failed read is not an empty list (CLAUDE.md, "A Failure Must Never Look Like an Absence"). */}
                                    {assetsFailed && (
                                        <Typography role={'alert'} variant={'body2'} sx={{ mt: 1 }}>
                                            {t('create_blocker.objects_failed')}
                                        </Typography>
                                    )}
                                    {city && !loadingAssets && !assetsFailed && objectNames.length === 0 && (
                                        <Typography variant={'body2'} color={'text.secondary'} sx={{ mt: 1 }}>
                                            {t('create_blocker.validation.select_apartment')}
                                        </Typography>
                                    )}
                                </Box>

                                <Divider />

                                {/* Step 3: Dates */}
                                <Box>
                                    <Typography variant={'subtitle1'} fontWeight={600} sx={{ mb: 2 }}>
                                        {t('create_blocker.start_date')} & {t('create_blocker.end_date')}
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <DateRangeField
                                                from={startDate ? dayjs(startDate) : null}
                                                to={endDate ? dayjs(endDate) : null}
                                                onChange={(from, to) => handleDateChange(from, to)}
                                                disablePast
                                                disabled={!selectedAsset}
                                                allowedStartDays={availableCheckInDates}
                                                allowedEndDays={availableCheckOutDates}
                                                maxDate={dayjs().add(18, 'month').endOf('month')}
                                                label={t('create_blocker.period') + ' *'}
                                            />
                                        </Grid>
                                        {isVenue && (
                                            <>
                                                <Grid item xs={12} sm={6}>
                                                    <SelectField
                                                        options={availableStartTimes}
                                                        value={startTime ?? ''}
                                                        onChange={handleStartTimeChange}
                                                        label={t('create_booking.reservation_details.start_time') + ' *'}
                                                        disabled={!startDate || !endDate}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <SelectField
                                                        options={availableEndTimes}
                                                        value={endTime ?? ''}
                                                        onChange={handleEndTimeChange}
                                                        label={t('create_booking.reservation_details.end_time') + ' *'}
                                                        disabled={!startDate || !endDate}
                                                    />
                                                </Grid>
                                            </>
                                        )}
                                    </Grid>
                                </Box>

                                <Divider />

                                {/* Step 4: Reason */}
                                <Box>
                                    <Typography variant={'subtitle1'} fontWeight={600} sx={{ mb: 2 }}>
                                        {t('create_blocker.reason')}
                                    </Typography>
                                    <SelectField
                                        options={BLOCKING_REASONS}
                                        optionLabels={Object.fromEntries(
                                            BLOCKING_REASONS.map((k) => [k, t(`issue_type.${k}`)]),
                                        )}
                                        value={reason}
                                        onChange={setReason}
                                        label={t('create_blocker.reason') + ' *'}
                                    />
                                </Box>

                                <Divider />

                                {/* Step 5: Comment */}
                                <Box>
                                    <Typography variant={'subtitle1'} fontWeight={600} sx={{ mb: 2 }}>
                                        {t('create_blocker.comment')}
                                    </Typography>
                                    <TextareaField
                                        value={comment ?? ''}
                                        onChange={(value) => dispatch(setComment({ comment: value }))}
                                        label={t('create_blocker.comment')}
                                        rows={3}
                                    />
                                </Box>
                            </Stack>
                        </Paper>
                    </Grid>

                    {/* Summary Sidebar */}
                    <Grid item xs={12} lg={4}>
                        <Paper sx={styles.paper}>
                            <Stack spacing={2}>
                                <Typography variant={'h6'} fontWeight={600}>
                                    {t('create_blocker.summary.title')}
                                </Typography>

                                <Divider />

                                <Box>
                                    <Typography variant={'body2'} color={'text.secondary'}>
                                        {t('create_blocker.summary.location')}
                                    </Typography>
                                    {renderSummaryValue(city ?? null)}
                                </Box>

                                <Box>
                                    <Typography variant={'body2'} color={'text.secondary'}>
                                        {t('create_blocker.summary.apartment')}
                                    </Typography>
                                    {renderSummaryValue(selectedAsset?.assetName ?? null)}
                                </Box>

                                <Box>
                                    <Typography variant={'body2'} color={'text.secondary'}>
                                        {t('create_blocker.summary.period')}
                                    </Typography>
                                    {renderSummaryValue(
                                        startDate && endDate
                                            ? isVenue && startTime && endTime
                                                ? `${dayjs(startDate).format('DD MMM YYYY')} ${startTime} – ${dayjs(endDate).format('DD MMM YYYY')} ${endTime}`
                                                : `${dayjs(startDate).format('DD MMM YYYY')} – ${dayjs(endDate).format('DD MMM YYYY')}`
                                            : null,
                                    )}
                                    {nightCount > 0 && (
                                        <Typography variant={'body2'} color={'text.secondary'}>
                                            {t('create_blocker.summary.nights', { count: nightCount })}
                                        </Typography>
                                    )}
                                </Box>

                                <Box>
                                    <Typography variant={'body2'} color={'text.secondary'}>
                                        {t('create_blocker.summary.reason')}
                                    </Typography>
                                    {renderSummaryValue(
                                        reason ? t(`issue_type.${reason}`) : null,
                                    )}
                                </Box>

                                <Box>
                                    <Typography variant={'body2'} color={'text.secondary'}>
                                        {t('create_blocker.comment')}
                                    </Typography>
                                    {renderSummaryValue(comment || null)}
                                </Box>

                                <Divider />

                                <Stack direction={'row'} spacing={2} sx={{ pt: 1 }}>
                                    <CustomButton
                                        title={t('create_blocker.button.cancel')}
                                        isFilled={false}
                                        onClick={() => navigate(paths.rentalBooking)}
                                    />
                                    <CustomButton
                                        title={t('create_blocker.button.create')}
                                        isFilled={true}
                                        disabled={!isFormValid}
                                        loading={loadingCreate}
                                        onClick={handleCreateBlocker}
                                    />
                                </Stack>
                            </Stack>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            <SnackbarComponent
                open={snackbarData.open}
                message={snackbarData.message}
                severity={snackbarData.severity}
                onClose={handleCloseSnackbar}
            />
        </Container>
    );
};

export default CreateBlockerPage;
